import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface Props {
  /** The presigned photo URL to show full-screen. */
  url: string;
  /** Filename hint for the download (no extension needed — derived from the URL). */
  downloadName?: string;
  /** Alt text for the full-screen image. */
  alt?: string;
  onClose: () => void;
}

/**
 * Full-screen photo viewer for the parent Moments/feed photos. View + download
 * only — deliberately NO reactions or comments (out of scope). Uses headlessui
 * Dialog so focus-trap and Escape-to-close come for free; the dark backdrop
 * closes on click, the image itself does not.
 *
 * Download note: the photo URLs are cross-origin presigned S3 links, so a plain
 * `<a download>` would just navigate to the image (browsers ignore the download
 * attribute cross-origin). We fetch the bytes into a blob and download that,
 * the same pattern the students CSV export uses.
 */
const PhotoLightbox: React.FC<Props> = ({ url, downloadName, alt, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // `cache: 'reload'` is load-bearing. The <img> that displays the photo is
      // often fetched by the browser WITHOUT an Origin header, and S3 returns
      // that response with no Access-Control-Allow-Origin (and no Vary: Origin),
      // so the browser HTTP cache stores a header-less copy keyed only on the
      // URL. A subsequent default fetch() (which DOES send Origin) can be served
      // that cached, ACAO-less response and get blocked as a CORS violation.
      // Forcing a fresh network request bypasses the poisoned cache entry, and
      // S3 answers the real CORS request with ACAO (verified via curl).
      const res = await fetch(url, { cache: 'reload' });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      // Derive an extension from the blob type (image/jpeg -> jpg), fall back to jpg.
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      a.download = `${downloadName || 'photo'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Network blip / expired presigned URL — open the image in a new tab as a
      // fallback so the parent can still save it manually via the browser.
      window.open(url, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      {/* Dark full-screen backdrop — clicking it closes (headlessui wires this
          onto onClose for the panel's outside area). */}
      <div className="fixed inset-0 bg-black/90" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          data-testid="photo-lightbox"
          className="relative flex max-h-full max-w-full flex-col items-center"
        >
          {/* Toolbar: close (left) + download (right), floating above the image. */}
          <div className="absolute -top-1 right-0 left-0 z-10 flex items-center justify-between px-1">
            <button
              type="button"
              data-testid="photo-lightbox-close"
              onClick={onClose}
              aria-label="Close"
              className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <button
              type="button"
              data-testid="photo-lightbox-download"
              onClick={handleDownload}
              disabled={downloading}
              aria-label="Download photo"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {downloading ? 'Saving…' : 'Download'}
            </button>
          </div>

          <img
            src={url}
            alt={alt || 'Photo'}
            data-testid="photo-lightbox-image"
            className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
          />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default PhotoLightbox;
